import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { LoginCredentials, RegisterCredentials, AuthResponse, RefreshTokenResponse, User } from '../types'

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'

// Create axios instance with default configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token management
const getStoredTokens = () => {
  const accessToken = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')
  return { accessToken, refreshToken }
}

const setStoredTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('accessToken', accessToken)
  localStorage.setItem('refreshToken', refreshToken)
}

const clearStoredTokens = () => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

// Request interceptor for adding auth tokens
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = getStoredTokens()
    if (accessToken && accessToken !== 'undefined') {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor for handling token refresh
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const { refreshToken } = getStoredTokens()
      if (refreshToken) {
        try {
          const response = await axiosInstance.post('api/auth/refresh', {
            refreshToken
          })
          
          const { accessToken, refreshToken: newRefreshToken } = response.data
          setStoredTokens(accessToken, newRefreshToken)
          
          // Retry the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`
          }
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          // Refresh failed, clear tokens
          clearStoredTokens()
          return Promise.reject(refreshError)
        }
      } else {
        // No refresh token, clear storage
        clearStoredTokens()
      }
    }

    return Promise.reject(error)
  }
)

// Generic API Response type
export interface ApiResponse<T = any> {
  data: T
  message?: string
  success?: boolean
  [key: string]: any
}

// API Client class
export class ApiClient {
  static async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await axiosInstance.get<T>(url, { params })
    return response.data
  }

  static async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await axiosInstance.post<T>(url, data)
    return response.data
  }

  static async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await axiosInstance.put<T>(url, data)
    return response.data
  }

  static async delete<T = any>(url: string): Promise<T> {
    const response = await axiosInstance.delete<T>(url)
    return response.data
  }

  static async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await axiosInstance.patch<T>(url, data)
    return response.data
  }

  // Authentication methods
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>('api/auth/login', credentials)
      return response.data
    } catch (error: any) {
      // Handle network errors and HTTP errors consistently
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        throw new Error('Unable to connect to the server. Please check your internet connection.')
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password.')
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || error.response.data?.error || 'Invalid request. Please check your input.')
      }
      if (error.response?.status === 409) {
        throw new Error(error.response.data?.message || error.response.data?.error || 'Conflict: The request conflicts with the current state of the resource.')
      }
      if (error.response?.status === 422) {
        throw new Error(error.response.data?.message || error.response.data?.error || 'Validation error. Please check your input.')
      }
      if (error.response?.status >= 500) {
        throw new Error(error.response.data?.message || error.response.data?.error || 'Server error. Please try again later.')
      }
      // For any other HTTP error status, try to extract the server message
      if (error.response?.status) {
        throw new Error(error.response.data?.message || error.response.data?.error || 'Request failed')
      }
      // Re-throw the original error if it's something else
      throw error
    }
  }

  static async register(credentials: Omit<RegisterCredentials, 'confirmPassword'>): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>('api/auth/register', credentials)
      return response.data
    } catch (error: any) {
      // Handle network errors and HTTP errors consistently
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        throw new Error('Unable to connect to the server. Please check your internet connection.')
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Registration failed. Please check your input.')
      }
      if (error.response?.status === 409) {
        throw new Error('An account with this email already exists.')
      }
      if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.')
      }
      // Re-throw the original error if it's something else
      throw error
    }
  }

  static async getCurrentUser(): Promise<{ data: User }> {
    const response = await axiosInstance.get<{ data: User }>('api/auth/me?include=billing_entities')
    return response.data
  }

  static async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await axiosInstance.post<RefreshTokenResponse>('api/auth/refresh', { refreshToken })
    return response.data
  }

  static async logout(): Promise<void> {
    await axiosInstance.post('api/auth/logout')
  }
}

// Export the axios instance for advanced usage
export { axiosInstance }
